import axios from 'axios';
import app from "@/main";
import { IlamBilgisiEntity } from "@/entity/IlamBilgisiEntity";
import { taleplerdenIlamBilgileri } from "@/utils/ilamBilgisi";

/**
 * Takip servisi - Takip verilerini yönetir
 * Sadece API çağrısı yapan fonksiyonlar içerir
 */
class TakipDetayService {
  /**
   * Takip detaylarını, alacaklı/borçlu ile birlikte yükler.
   * İş listesi hariç temel verileri çeker.
   */
  async load(id: string): Promise<any> {
    // Sadece API'den verileri döndür
    return this.getTakipTumVeriler(id, false);
  }

  /**
   * Takip kaydını ID'ye göre getirir
   */
  async getTakipById(id: string) {
    if (!id) throw new Error('Takip ID boş olamaz');
    return app.$http.get(`/api/v1/takip/${id}`);
  }

  /**
   * Takip ID'sine göre alacaklıları getirir
   */
  async getAlacaklilarByTakipId(id: string) {
    if (!id) return [];
    const res = await app.$http.get(`/api/v1/takip/${id}/alacaklilar`);
    return Array.isArray(res.data) ? res.data : (res.data.alacaklilar || []);
  }

  /**
   * Takip ID'sine göre borçluları getirir
   */
  async getBorcluByTakipId(id: string) {
    if (!id) return [];
    const res = await app.$http.get(`/api/v1/takip/${id}/borclular`);
    return Array.isArray(res.data) ? res.data : (res.data.borclular || []);
  }

  /**
   * Takip ID'sine göre alacakları getirir
   */
  async getAlacaklarByTakipId(id: string) {
    if (!id) return [];
    const res = await app.$http.get(`/api/v1/takip/${id}/alacaklar`);
    return Array.isArray(res.data) ? res.data : (res.data.alacaklar || []);
  }

  /**
   * DEV-2916 — takip ana kartında gösterilecek ilam bilgisi özetini getirir.
   *
   * Mevcut `alacak-talep-detay` uç noktasını kullanır: Alacaklar ekranındaki
   * ilam alanlarının kaynağı olan `alacaklar` kayıtlarını ilişkileriyle
   * birlikte döndürür. Özete dönüşüm istemci tarafında yapılır, bu iş için
   * ayrı bir API'ye gerek yoktur.
   *
   * `alacak-detay` yerine bu tercih edildi: o uç nokta yalnızca ilk talebi
   * okuyor, bu ise takibin tüm taleplerini kapsıyor (ikili takip).
   *
   * İstek başarısız olursa boş dizi döner; kart o zaman hiç render edilmez.
   */
  async getIlamBilgileriByTakipId(id: string): Promise<IlamBilgisiEntity[]> {
    if (!id) return [];
    try {
      const res = await app.$http.get(`/api/v1/takip/${id}/alacak-talep-detay`);
      const talepler = Array.isArray(res) ? res : (res?.data ?? []);
      return taleplerdenIlamBilgileri(talepler);
    } catch (error) {
      return [];
    }
  }

  /**
   * Takip ID'sine göre iş listesini getirir
   */
  async getIsListesiByTakipId(id: string): Promise<any[]> {
    if (!id) return [];
    const res = await app.$http.get(`/api/v1/takip/${id}/is-listesi`);
    return Array.isArray(res.data) ? res.data : (res.data.isListesi || []);
  }

  /**
   * Takip ile ilgili tüm verileri tek seferde getirir
   */
  async getTakipTumVeriler(id: string, loadIsListesi = false) {
    if (!id) {
      return {
        takip: null,
        alacaklilar: [],
        borclular: [],
        alacaklar: [],
        ilamBilgileri: [],
        isListesi: [],
        notlar: []
      };
    }
    const takipRes = await this.getTakipById(id).catch(() => null);
    const [alacaklilarRes, borclularRes, alacaklarRes, ilamBilgileriRes, isListesiRes] = await Promise.all([
      this.getAlacaklilarByTakipId(id).catch(() => []),
      this.getBorcluByTakipId(id).catch(() => []),
      this.getAlacaklarByTakipId(id).catch(() => []),
      this.getIlamBilgileriByTakipId(id).catch(() => []),
      this.getIsListesiByTakipId(id).catch(() => []),
    ]);
    return {
      takip: takipRes ? takipRes.data : null,
      alacaklilar: alacaklilarRes,
      borclular: borclularRes,
      alacaklar: alacaklarRes,
      ilamBilgileri: ilamBilgileriRes,
      isListesi: isListesiRes
    };
  }

  /**
   * Takip ID'sine göre notları getirir
   */
  async getNotlarByTakipId(id: string) {
    if (!id) return [];
    const res = await app.$http.get(`/api/v1/takip/${id}/notlar`);
    return Array.isArray(res.data) ? res.data : (res.data.notlar || []);
  }

  /**
   * Takip ID'sine göre faiz verisini getirir
   */
  async getFaizData(id: string, hesapTarihi?: Date) {
    try {
      let response;
      if (hesapTarihi) {
        response = await app.$http.post(`/api/v1/takip/${id}/faiz`, { hesap_tarihi: hesapTarihi });
      } else {
        response = await app.$http.post(`/api/v1/takip/${id}/faiz`);
      }
      if (response && response.data) {
        return response.data;
      } else {
        throw new Error('Faiz verisi alınamadı');
      }
    } catch (error) {
      console.error(`Takip ID ${id} için faiz verisi alınırken hata:`, error);
      throw error;
    }
  }

  /**
   * Borçlu ID'sine göre vekilleri getirir
   */
  async getVekillerByBorcluId(borcluId: number) {
    if (!borcluId) return [];
    try {
      const res = await app.$http.get(`/api/v1/borclu/${borcluId}/borclu-vekili`);
      return Array.isArray(res.data) ? res.data : (res.data.vekiller || []);
    } catch (error) {
      console.error(`Borçlu ${borcluId} için vekiller alınırken hata:`, error);
      return [];
    }
  }

  /**
   * Borçlu ID'sine göre tebligatları getirir
   */
  async getTebligatlarByBorcluId(borcluId: number) {
    if (!borcluId) return [];
    try {
      const res = await app.$http.get(`/api/v1/borclu/${borcluId}/tebligatlar`);
      return Array.isArray(res.data) ? res.data : (res.data.tebligatlar || []);
    } catch (error) {
      console.error(`Borçlu ${borcluId} için tebligatlar alınırken hata:`, error);
      return [];
    }
  }

  /**
   * Borçlu ID'sine göre borçlu bilgilerini getirir
   */
  async getBorcluBilgileriById(borcluId: number) {
    if (!borcluId) return null;
    try {
      const res = await app.$http.get(`/api/v1/borclu/${borcluId}`);
      return res.data?.borclu_bilgileri || null;
    } catch (error) {
      console.error(`Borçlu ${borcluId} için bilgiler alınırken hata:`, error);
      return null;
    }
  }

  /**
   * Takip ID'sine göre nafaka eklemeyi durdurur
   */
  async durdurNafaka(takipId: string) {
    if (!takipId) throw new Error('Takip ID boş olamaz');
    return app.$http.put(`/api/v1/takip/${takipId}/nafaka-durdur`);
  }

  /**
   * Borçlu ID'sine göre adresleri getirir
   */
  async getAdreslerByBorcluId(borcluId: number) {
    if (!borcluId) return { adresler: [], varsayilan_adres: null };
    try {
      const res = await app.$http.get(`/api/v1/borclu/${borcluId}/adresler`);
      // API response: { data: { adresler: [...], varsayilan_adres: {...} } }
      const responseData = res.data?.data || res.data;
      return {
        adresler: Array.isArray(responseData?.adresler) ? responseData.adresler : [],
        varsayilan_adres: responseData?.varsayilan_adres || null
      };
    } catch (error) {
      console.error(`Borçlu ${borcluId} için adresler alınırken hata:`, error);
      return { adresler: [], varsayilan_adres: null };
    }
  }
}

export default new TakipDetayService();